import * as z from 'zod'
import { baseEntitySchema } from '../../core/entity.js'

export const availabilityValues = ['yes', 'maybe', 'no'] as const
export type Availability = (typeof availabilityValues)[number]

export const optionRatingValues = ['up', 'down'] as const
export type OptionRating = (typeof optionRatingValues)[number]

export const pollResponseSchema = baseEntitySchema.extend({
  type: z.literal('pollresponse'),
  pollType: z.enum(['one-of-many', 'many-of-many']).default('many-of-many'),

  /** Choices for many of many  */
  choices: z.record(z.string(), z.boolean()).optional(),
  /** choice for one of many */
  choice: z.number().optional(),

  /**
   * Three-state availability per option index, for date polls. `choices` stays the derived
   * yes/no projection every pre-existing consumer reads — `getProgress`, `sortPollOptionsIndices`,
   * the Final Option view — so the two are always written together and can never drift.
   */
  availability: z
    .record(z.string(), z.enum(availabilityValues))
    .describe('Three-state availability per option index. `choices` stays the derived yes/no projection')
    .optional(),

  /**
   * The voter's thumbs up / down per option index: a sorting aid for triaging options before voting. It is not
   * a vote, so nothing that counts votes reads it.
   */
  ratings: z
    .record(z.string(), z.enum(optionRatingValues))
    .describe("The voter's thumbs up / down per option index, used to sort options before voting. Not a vote")
    .optional(),

  /** The voter's free-text note per option index, for their own triage. Not a vote; empty notes are not stored. */
  comments: z
    .record(z.string(), z.string().max(2000))
    .describe("The voter's free-text note per option index. Not a vote")
    .optional(),
})

export const manyOfManyPollResponseSchema = pollResponseSchema.extend({
  pollType: z.literal('many-of-many'),
  /** Choices for many of many  */
  choices: z.record(z.string(), z.boolean()),
  choice: z.undefined().optional(),
})

export const oneOfManyPollResponseSchema = pollResponseSchema.extend({
  pollType: z.literal('one-of-many'),
  /** The single choice made  */
  choice: z.number(),
  choices: z.undefined().optional(),
})

export type ManyOfManyPollResponse = z.infer<typeof manyOfManyPollResponseSchema>
export type OneOfManyPollResponse = z.infer<typeof oneOfManyPollResponseSchema>

export type PollResponse = z.infer<typeof pollResponseSchema>

export function isPollResponse(node?: unknown): node is PollResponse {
  return pollResponseSchema.safeParse(node).success
}

export function isManyOfManyPollResponse(node?: unknown): node is ManyOfManyPollResponse {
  return manyOfManyPollResponseSchema.safeParse(node).success
}

export function isOneOfManyPollResponse(node?: unknown): node is OneOfManyPollResponse {
  return oneOfManyPollResponseSchema.safeParse(node).success
}
